import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  styled,
} from "@mui/material";

import { useSelector } from "react-redux";
import { buildContactSalesMailto } from "../../utils/planUtils";

import CheckIcon from "@mui/icons-material/Check";

import CurrentPlanButton from "./CurrentPlanButton";

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  width: "100%",
  maxWidth: "400px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  transition: "transform 0.2s ease-in-out, box-shadow 0.2s",
  cursor: "pointer",

  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: theme.shadows[6],
  },

  [theme.breakpoints.up("md")]: {
    minHeight: 400,
  },

  [theme.breakpoints.down("sm")]: {
    minWidth: "100%",
    maxWidth: "100%",
  },
}));

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "light" ? "#f5f5f5" : "#2d3843",

  padding: theme.spacing(3),
  borderTop: "7px solid #003D78",
}));

const FeatureList = styled("ul")({
  margin: 0,
  padding: 0,
  listStyle: "none",
});

const Feature = styled("li")(({ theme }) => ({
  margin: theme.spacing(1, 0),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

export default function PlanCard({
  plan,
  isCurrent,
  expiryDate,
  isMobile,
  onSelect,
}) {
  const isCustom = plan.isCustom;
  const features = plan.features;

  const userInfo = useSelector((state) => state.userLogin?.userInfo);
  const contactMailto = isCustom
    ? buildContactSalesMailto({
        planTitle: plan.title,
        name: userInfo?.name,
        email: userInfo?.email,
      })
    : null;

  const isClickable = !isCurrent && !isCustom;

  return (
    <Grid
      item
      xs={12}
      sm={6}
      md={4}
      lg={4}
      sx={{
        display: "flex",
        justifyContent: "center",
      }}
    >
      <StyledCard
        onClick={() => isClickable && onSelect(plan)}
        sx={{
          border: isCurrent ? "2.5px solid #003D78" : "1px solid #e0e0e0",

          backgroundColor: isCurrent
            ? "rgba(0, 61, 120, 0.02)"
            : "background.paper",

          boxShadow: isCurrent ? "0 8px 24px rgba(0, 61, 120, 0.18)" : 1,

          position: "relative",

          cursor: isClickable ? "pointer" : "default",

          "&:hover": !isClickable
            ? {
                transform: "none",
                boxShadow: isCurrent
                  ? "0 8px 24px rgba(0, 61, 120, 0.18)"
                  : 1,
              }
            : undefined,
        }}
      >
        <StyledCardHeader
          title={
            <Typography
              component='h2'
              sx={{
                fontSize: {
                  xs: "16px",
                  lg: "18px",
                },
                display: "flex",
                justifyContent: "center",
                fontWeight: "bold",
              }}
            >
              {plan.title}
            </Typography>
          }
          subheader={
            <div style={{ marginTop: 16 }}>
              <Typography
                color='primary'
                sx={{
                  fontWeight: "bold",
                  fontSize: {
                    xs: "20px",
                    lg: "24px",
                  },
                }}
              >
                {plan.price}
              </Typography>

              <Typography
                color='text.secondary'
                sx={{
                  fontSize: {
                    xs: "14px",
                    lg: "16px",
                  },
                }}
              >
                {plan.priceNote}
              </Typography>
            </div>
          }
          titleTypographyProps={{
            align: "center",
          }}
          subheaderTypographyProps={{
            align: "center",
          }}
        />

        <CardContent
          sx={{
            flexGrow: 1,
            pt: 2,
          }}
        >
          <FeatureList>
            {features.map((feature) => (
              <Feature key={feature}>
                <CheckIcon
                  color='primary'
                  fontSize={isMobile ? "small" : "medium"}
                />

                <Typography
                  variant='body2'
                  sx={{
                    fontSize: {
                      xs: "13px",
                      lg: "14px",
                    },
                  }}
                >
                  {feature}
                </Typography>
              </Feature>
            ))}
          </FeatureList>
        </CardContent>

        <CardActions
          sx={{
            p: 3,
            pt: 0,
          }}
        >
          {isCurrent ? (
            <CurrentPlanButton expiryDate={expiryDate} isMobile={isMobile} />
          ) : (
            <div style={{ width: "100%" }}>
              <button
                type='button'
                onClick={(event) => {
                  event.stopPropagation();
                  if (isCustom) {
                    window.location.href = contactMailto;
                  } else {
                    onSelect(plan);
                  }
                }}
                style={{
                  width: "100%",
                  border: "none",
                  borderRadius: 8,
                  padding: isMobile ? "6px 16px" : "8px 22px",
                  backgroundColor: "#003D78",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                {plan.buttonText}
              </button>
            </div>
          )}
        </CardActions>
      </StyledCard>
    </Grid>
  );
}
